import { CreateDialogFieldCompanion } from 'components/create-dialog/CreateDialogRequired';
import DmsTeamSelect from 'components/dms/DmsTeamSelect';
import DmsTeamStatusIndicator from 'components/dms/DmsTeamStatusIndicator';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import InputWrapper from 'components/forms/inputs/rhf-inputs/InputWrapper';
import MultiSelectInput from 'components/forms/inputs/rhf-inputs/MultiSelectInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import ReadonlyField from 'components/forms/inputs/rhf-other/ReadonlyField';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import { useLazyGetMinTeamsListWithFiltersQuery } from 'store/api/teams';
import { TeamType } from 'types/teams';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { TeamFormState } from './useTeamForm';

/** The type with the fields it drives: DMS group, name, initials, parent and the practice or business areas. */
export const TeamFields = ({ form }: { form: TeamFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    const { teamType, isParent } = form;
    // On the record page a field is a cell of the card's grid, where the read view has its row: what the form says
    // about the DMS group goes on that field's label line there, rather than on a row of its own under it.
    const inSection = useInRecordSection();
    // A team with no group also carries dmsTeamValid false.
    const isDmsTeamInvalid = !!form.team?.dmsTeamId && form.team.dmsTeamValid === false;
    return (
        <>
            <SelectInput
                setValue={setValue}
                control={control}
                label="teams.team-type"
                placeholder="forms.select-team-type"
                name="teamType"
                options={
                    (getValidOptionsFromClassifier(form.classifiers['TEAM_TYPE']) as {
                        label: string;
                        value: string;
                    }[]) ?? []
                }
                getOptionLabel="label"
                getOptionIdentifier="value"
                required
            />
            {form.hasDmsTeamField && (
                <>
                    <DmsTeamSelect
                        name="dmsTeam"
                        control={control}
                        setValue={setValue}
                        rightLabelComponent={
                            inSection && isDmsTeamInvalid ? (
                                // Takes what the label leaves of the line and cuts there: the icon's tooltip has it all.
                                <span className="text-error flex min-w-0 flex-1 items-center justify-end gap-1 text-xs">
                                    <DmsTeamStatusIndicator isValid={false} />
                                    <span className="truncate">{t('teams.dms-team-invalid')}</span>
                                </span>
                            ) : undefined
                        }
                    />
                    {!inSection && isDmsTeamInvalid && (
                        <div className="text-error -mt-3 flex items-center gap-2">
                            <DmsTeamStatusIndicator isValid={false} />
                            <span className="text-sm">{t('teams.dms-team-invalid')}</span>
                        </div>
                    )}
                </>
            )}
            <TextInput control={control} label="name" placeholder="forms.enter-name" name="name" required />
            <TextInput
                control={control}
                label="initials"
                placeholder="forms.enter-initials"
                name="abbreviation"
                required
            />
            {/* Ticking it is what lifts the parent team's requirement, so in the create dialog it is drawn
                beside that field, in Required while the field is there. */}
            <CreateDialogFieldCompanion field="parentTeam">
                <CheckboxInput
                    control={control}
                    name="isParent"
                    label="forms.is-parent"
                    disabled={teamType?.value === TeamType.CORRESPONDENCE}
                />
            </CreateDialogFieldCompanion>
            {!isParent && (
                <DynamicSelectInput
                    setValue={setValue}
                    control={control}
                    label="teams.parent-team"
                    placeholder="forms.select-parent-team"
                    name="parentTeam"
                    paginated={{
                        fetch: useLazyGetMinTeamsListWithFiltersQuery,
                        searchField: 'name',
                    }}
                    getOptionLabel="name"
                    getOptionIdentifier="id"
                    required={!isParent}
                    loading={form.isLoadingNewParentTeamData}
                />
            )}
            {form.isPracticeGroup && (
                <MultiSelectInput
                    setValue={setValue}
                    control={control}
                    label="teams.practice-areas"
                    placeholder="forms.select-practice-area"
                    name="teamPracticeAreas"
                    options={form.practiceAreaList ?? []}
                    getOptionLabel="name"
                    getOptionIdentifier="id"
                />
            )}
            {teamType?.value === 'PRACTICE_GROUP' && !isParent && (
                <InputWrapper
                    name="parentPracticeAreaList"
                    label="teams.practice-areas"
                    readonly={true}
                    hasErrors={false}
                >
                    <ReadonlyField
                        getLabel={(option) => option?.name}
                        getValue={(option) => option?.id}
                        value={form.parentPracticeAreaList ?? []}
                    />
                </InputWrapper>
            )}
            {teamType?.value === 'SECTOR_GROUP' ? (
                <MultiSelectInput
                    control={control}
                    name="teamBusinessAreas"
                    label="teams.business-areas"
                    placeholder="forms.select-business-area"
                    options={form.businessAreaOptions}
                    setValue={setValue}
                    getOptionLabel="label"
                    getOptionIdentifier="value"
                    sortDirection="asc"
                />
            ) : null}
        </>
    );
};

/** The offices and the team's e-mail address. */
export const TeamContactFields = ({ form }: { form: TeamFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    return (
        <>
            <DynamicMultiSelectInput
                setValue={setValue}
                control={control}
                label="teams.offices"
                placeholder="forms.select-office"
                name="teamOffices"
                paginated={{
                    fetch: useLazyGetMinOfficesWithFiltersQuery,
                    searchField: 'name',
                }}
                getOptionLabel={(option: { name?: string; isActive?: boolean }) =>
                    `${option.name ?? ''}${officeInactiveSuffix(option) ?? ''}`
                }
                getOptionIdentifier="id"
            />
            <TextInput
                control={control}
                label="teams.team-email"
                placeholder="forms.enter-email"
                name="emailAddress"
                validate={form.validEmail}
            />
        </>
    );
};

export const TeamActiveField = ({ form }: { form: TeamFormState }): JSX.Element => (
    <CheckboxInput control={form.methods.control} name="isActive" label="active" />
);
